import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { InterviewCard, OperatorCard, LoadingBlock } from "@/components/site/Cards";

export default function IndustryDetail() {
  const { slug } = useParams();
  const [ind, setInd] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/industries/${slug}`).then((r) => setInd(r.data)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <LoadingBlock />;
  if (!ind) return <div className="p-20 text-center">Industry not found.</div>;

  return (
    <div data-testid="industry-detail">
      <section className="relative border-b border-oc-hairline bg-oc-ink text-white overflow-hidden">
        {ind.hero_image && (
          <img src={ind.hero_image} alt={ind.name} className="absolute inset-0 w-full h-full object-cover opacity-40" />
        )}
        <div className="relative max-w-editorial mx-auto px-6 md:px-10 py-20 md:py-28">
          <Link to="/industries" className="inline-flex items-center gap-2 font-ui text-xs font-bold uppercase tracking-widest text-white/70 hover:text-oc-coral mb-8">
            <ArrowLeft className="w-3.5 h-3.5" /> All Industries
          </Link>
          <div className="eyebrow text-oc-coral mb-6">Industry</div>
          <h1 className="hero-headline text-5xl md:text-7xl">{ind.name}</h1>
          {ind.description && (
            <p className="mt-6 font-editorial text-xl md:text-2xl text-white/80 leading-snug max-w-3xl">
              {ind.description}
            </p>
          )}
        </div>
      </section>

      {ind.common_challenges?.length > 0 && (
        <section className="border-b border-oc-hairline bg-oc-sand">
          <div className="max-w-editorial mx-auto px-6 md:px-10 py-16 md:py-20">
            <div className="eyebrow text-oc-coral mb-4">Common Operating Challenges</div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
              {ind.common_challenges.map((c, i) => (
                <div key={i} className="border-t-2 border-oc-ink pt-4">
                  <div className="font-ui text-xs font-bold text-oc-coral uppercase tracking-widest mb-2">{String(i + 1).padStart(2, "0")}</div>
                  <div className="font-editorial text-xl leading-snug">{c}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {ind.interviews?.length > 0 && (
        <section className="border-b border-oc-hairline">
          <div className="max-w-editorial mx-auto px-6 md:px-10 py-16 md:py-20">
            <div className="eyebrow text-oc-coral mb-4">Featured Conversations</div>
            <h2 className="font-editorial text-3xl md:text-4xl mb-12">Interviews in {ind.name}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {ind.interviews.map((iv) => <InterviewCard key={iv.slug} interview={iv} />)}
            </div>
          </div>
        </section>
      )}

      {ind.operators?.length > 0 && (
        <section className="bg-oc-sand">
          <div className="max-w-editorial mx-auto px-6 md:px-10 py-16">
            <div className="eyebrow text-oc-coral mb-4">Featured Operators</div>
            <h2 className="font-editorial text-3xl md:text-4xl mb-12">Operators in {ind.name}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {ind.operators.map((op) => <OperatorCard key={op.slug} operator={op} />)}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
